import { useState } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { useMutation, useQuery } from "convex/react"
import { api } from "../../convex/_generated/api"
import type { Id } from "../../convex/_generated/dataModel"
import { Skeleton } from "@/components/ui/skeleton"
import { PaywallDialog } from "@/components/paywall-dialog"
import Footer from "@/components/footer-1"
import { toast } from "sonner"
import { MapPin, Calendar } from "lucide-react"
import { formatHackathonWhen } from "@/lib/hackathon-placement"
import { errorMessage } from "@/lib/errors"

export const Route = createFileRoute("/hackathons/$hackathonId")({
  component: HackathonDetailPage,
})

function HackathonDetailPage() {
  const { hackathonId } = Route.useParams()
  const h = useQuery(api.hackathons.get, {
    hackathonId: hackathonId as Id<"hackathons">,
  })
  const register = useMutation(api.hackathons.register)
  const unregister = useMutation(api.hackathons.unregister)
  const profile = useQuery(api.profiles.me)
  const myRegistration = useQuery(
    api.hackathons.myRegistration,
    profile?.role === "seeker"
      ? { hackathonId: hackathonId as Id<"hackathons"> }
      : "skip",
  )
  const [payOpen, setPayOpen] = useState(false)
  const [busy, setBusy] = useState(false)

  const doRegister = async () => {
    setBusy(true)
    try {
      const res = await register({
        hackathonId: hackathonId as Id<"hackathons">,
      })
      if (!res.ok && res.needsPayment) {
        setPayOpen(true)
        return
      }
      toast.success("You're in")
    } catch (e) {
      toast.error(errorMessage(e, "Could not apply"))
    } finally {
      setBusy(false)
    }
  }

  if (h === undefined) {
    return (
      <div className="mkt-page home-wf">
        <div className="mkt-page-shell mkt-page-shell--detail">
          <Skeleton className="h-64 w-full" />
        </div>
        <Footer />
      </div>
    )
  }
  if (!h) {
    return (
      <div className="mkt-page home-wf">
        <div className="mkt-page-shell mkt-page-shell--narrow">
          <p>Hackathon not found.</p>
          <Link
            to="/hackathons"
            className="home-wf-btn home-wf-btn--outline mt-4 inline-flex"
          >
            Back to hackathons
          </Link>
        </div>
        <Footer />
      </div>
    )
  }

  const ended = Date.now() > h.endsAt
  const full =
    h.capacity !== undefined && h.registrationCount >= h.capacity
  const hostInitial = h.employerName.charAt(0).toUpperCase()
  const canApply =
    h.status === "published" &&
    profile?.role === "seeker" &&
    !myRegistration &&
    !ended &&
    !full
  const returnTo = `/hackathons/${hackathonId}`

  return (
    <div className="mkt-page home-wf">
      <div className="mkt-page-shell mkt-page-shell--detail">
        <Link
          to="/hackathons"
          className="mb-6 inline-block text-sm font-bold text-[color:var(--wf-mute)] underline-offset-4 hover:text-[color:var(--wf-ink)] hover:underline"
        >
          ← All hackathons
        </Link>

        {h.status === "draft" && (
          <p className="mkt-chip mb-4 inline-flex">Draft — only you can see this</p>
        )}

        <div className="mkt-event-detail">
          <aside className="mkt-event-rail">
            <div className="mkt-event-poster">
              {h.imageUrl ? (
                <img src={h.imageUrl} alt="" />
              ) : (
                <div className="mkt-event-poster__fallback" aria-hidden>
                  {h.title.charAt(0).toUpperCase()}
                </div>
              )}
            </div>

            <div className="mkt-event-host">
              <p className="mkt-event-host__label">Hosted by</p>
              <div className="mkt-event-host__row">
                <span className="mkt-event-host__mark" aria-hidden>
                  {hostInitial}
                </span>
                <p className="mkt-event-host__name">{h.employerName}</p>
              </div>
              {h.capacity !== undefined && (
                <p className="text-sm font-medium text-[color:var(--wf-mute)]">
                  {h.registrationCount}/{h.capacity} applied
                </p>
              )}
              {h.capacity === undefined && h.registrationCount > 0 && (
                <p className="text-sm font-medium text-[color:var(--wf-mute)]">
                  {h.registrationCount} applied
                </p>
              )}
            </div>
          </aside>

          <div className="mkt-event-main">
            <h1 className="mkt-page-title">{h.title}</h1>

            <div className="mkt-event-meta">
              <p className="mkt-event-meta__row">
                <Calendar aria-hidden />
                <span>{formatHackathonWhen(h.startsAt, h.endsAt)}</span>
              </p>
              <p className="mkt-event-meta__row">
                <MapPin aria-hidden />
                <span>
                  {h.venue} · {h.address}, {h.city}
                </span>
              </p>
            </div>

            {h.status === "published" && profile?.role === "seeker" && (
              <div className="mkt-event-apply">
                {myRegistration === undefined ? (
                  // Still loading: showing the Apply branch here offers an
                  // already-registered seeker a button that can only fail.
                  <Skeleton className="h-24 w-full" />
                ) : myRegistration ? (
                  <>
                    <h2>You&apos;re in</h2>
                    <p>
                      Your spot with {h.employerName} is confirmed
                      {ended ? " — this event has ended." : "."}
                    </p>
                    <div className="mkt-event-apply__actions">
                      {!ended && (
                        <button
                          type="button"
                          className="home-wf-btn home-wf-btn--outline"
                          disabled={busy}
                          onClick={async () => {
                            if (busy) return
                            setBusy(true)
                            try {
                              await unregister({
                                hackathonId: hackathonId as Id<"hackathons">,
                              })
                              toast.success("Application cancelled")
                            } catch (e) {
                              toast.error(
                                errorMessage(e, "Could not cancel"),
                              )
                            } finally {
                              setBusy(false)
                            }
                          }}
                        >
                          {busy ? "Cancelling…" : "Cancel"}
                        </button>
                      )}
                      <Link
                        to="/hackathons"
                        className="home-wf-btn home-wf-btn--outline inline-flex"
                      >
                        Browse more
                      </Link>
                    </div>
                  </>
                ) : ended ? (
                  <>
                    <h2>Event ended</h2>
                    <p>This hackathon is no longer open for applications.</p>
                  </>
                ) : full ? (
                  <>
                    <h2>Full</h2>
                    <p>Every seat is taken for this hackathon.</p>
                  </>
                ) : (
                  <>
                    <h2>Apply</h2>
                    <p>
                      {profile.seekerAccessActive
                        ? `Join ${h.employerName}'s hackathon with your Hire profile.`
                        : `Applying uses your ₹2,500/year access — one profile for jobs and hackathons.`}
                    </p>
                    <div className="mkt-event-apply__actions">
                      <button
                        type="button"
                        className="home-wf-btn home-wf-btn--lime"
                        onClick={doRegister}
                        disabled={busy || !canApply}
                      >
                        {busy ? "Applying…" : "Apply"}
                      </button>
                    </div>
                  </>
                )}
              </div>
            )}

            {h.status === "published" && profile === null && !ended && !full && (
              <div className="mkt-event-apply">
                <h2>Apply</h2>
                <p>
                  Sign up free to apply — the ₹2,500/year access only kicks
                  in when you send your first application.
                </p>
                <div className="mkt-event-apply__actions">
                  <Link
                    to="/jobs/join"
                    search={{ returnTo }}
                    className="home-wf-btn home-wf-btn--lime inline-flex"
                  >
                    Sign up to apply
                  </Link>
                </div>
              </div>
            )}

            <section className="mkt-event-about">
              <h2>About</h2>
              <div className="mkt-event-about__body">{h.description}</div>
            </section>

            {h.linkedJob && h.status === "published" && (
              <div className="mkt-event-linked">
                <p className="mkt-event-linked__label">Hiring through this event</p>
                <Link
                  to="/jobs/$jobId"
                  params={{ jobId: h.linkedJob._id }}
                >
                  {h.linkedJob.title}
                </Link>
              </div>
            )}
          </div>
        </div>

        <PaywallDialog
          open={payOpen}
          onOpenChange={setPayOpen}
          kind="seeker_annual"
          onPaid={async () => {
            const res = await register({
              hackathonId: hackathonId as Id<"hackathons">,
            })
            if (res.ok) {
              toast.success("You're in")
            }
          }}
        />
      </div>
      <Footer />
    </div>
  )
}
